import React, { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { auth } from '../../../firebase';
import { 
  DollarSign, Clock, CheckCircle2, AlertCircle, 
  RefreshCw, Wallet, Network, Shield, Bitcoin, 
  Ethereum, Coins, ArrowRight, Lock, Unlock, Info
} from 'lucide-react';
import "./withdraw.css";

import { API_BASE } from "../../../config/api";

const Withdraw = () => {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [lockedBalance, setLockedBalance] = useState(0);
  const [maxWithdrawal, setMaxWithdrawal] = useState(0);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [cryptocurrency, setCryptocurrency] = useState('USDT');
  const [network, setNetwork] = useState('TRC20');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Eligibility state
  const [eligible, setEligible] = useState(false);
  const [eligibilityMessage, setEligibilityMessage] = useState('');
  const [daysRemaining, setDaysRemaining] = useState(0);
  
  // Countdown state
  const [showCountdown, setShowCountdown] = useState(false);
  const [withdrawalId, setWithdrawalId] = useState(null);
  const [countdownEndsAt, setCountdownEndsAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState({ minutes: 20, seconds: 0 });
  const [withdrawalStatus, setWithdrawalStatus] = useState('pending');
  
  // Success state
  const [showSuccess, setShowSuccess] = useState(false);

  // Supported cryptocurrencies and networks
  const cryptoOptions = [
    { value: 'USDT', label: 'Tether (USDT)', icon: '💵', networks: ['TRC20', 'ERC20', 'BEP20'] },
    { value: 'BTC', label: 'Bitcoin (BTC)', icon: '₿', networks: ['Bitcoin'] },
    { value: 'ETH', label: 'Ethereum (ETH)', icon: 'Ξ', networks: ['Ethereum'] },
    { value: 'LTC', label: 'Litecoin (LTC)', icon: 'Ł', networks: ['Litecoin'] }
  ];

  const getNetworksForCrypto = () => {
    const crypto = cryptoOptions.find(c => c.value === cryptocurrency);
    return crypto ? crypto.networks : [];
  };

  // ==========================================================================
  // HELPER: Get JWT token from localStorage
  // ==========================================================================
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // ==========================================================================
  // LOAD USER DATA WITH JWT AUTHENTICATION
  // ==========================================================================
  const loadUserData = useCallback(async () => {
    try {
      setError('');
      const headers = getAuthHeaders();
      
      console.log('💰 Loading withdrawal data with JWT token...');
      
      const metricsRes = await axios.get(`${API_BASE}/api/identity/dashboard-metrics`, { headers });
      
      if (metricsRes.data.success) {
        const availableBalance = metricsRes.data.balances?.availableLiquidity || 0;
        const locked = metricsRes.data.balances?.lockedInvestment || 0;
        
        setBalance(availableBalance);
        setLockedBalance(locked);
        setMaxWithdrawal(availableBalance);
      }

      const eligibilityRes = await axios.get(`${API_BASE}/api/withdrawal/check-eligibility`, { headers });
      
      if (eligibilityRes.data.success) {
        setEligible(eligibilityRes.data.eligible);
        setEligibilityMessage(eligibilityRes.data.message);
        if (eligibilityRes.data.daysRemaining) {
          setDaysRemaining(eligibilityRes.data.daysRemaining);
        }
      }
      
      console.log('✅ Withdrawal data loaded');
    } catch (err) {
      console.error("❌ Failed to load user data:", err);
      
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.clear();
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      } else {
        setError("Failed to load account information");
      }
    }
  }, []);

  // ==========================================================================
  // ✅ FIXED: REAL-TIME AUTHENTICATION TRACKER (Checks JWT first!)
  // ==========================================================================
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    const email = localStorage.getItem('userEmail');

    if (token) {
      // User is logged in via Backend JWT
      setUser({
        uid: 'jwt-user',
        email: email || 'user@example.com',
        displayName: email ? email.split('@')[0] : 'User'
      });
      loadUserData();
      setLoading(false);
    } else {
      // Fallback to Firebase Auth (for Google login, etc.)
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          setUser(firebaseUser);
          await loadUserData();
          setLoading(false);
        } else {
          setUser(null);
          setLoading(false);
        }
      });
      return () => unsubscribe();
    }
  }, [loadUserData]);

  // ✅ Auto-refresh every 30 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      loadUserData();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, loadUserData]);

  // ✅ Manual refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadUserData();
    setTimeout(() => setRefreshing(false), 1000);
  };

  // Countdown timer
  useEffect(() => {
    if (!showCountdown || !countdownEndsAt) return;

    const interval = setInterval(() => {
      const now = new Date();
      const end = new Date(countdownEndsAt);
      const diff = end - now;

      if (diff <= 0) {
        setTimeRemaining({ minutes: 0, seconds: 0 });
        clearInterval(interval);
        return;
      }

      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeRemaining({ minutes, seconds });

      // Check withdrawal status every 10 seconds
      if (withdrawalId) {
        checkWithdrawalStatus();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showCountdown, countdownEndsAt, withdrawalId]);

  // ✅ Check withdrawal status with JWT token
  const checkWithdrawalStatus = async () => {
    if (!withdrawalId || !user) return;

    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_BASE}/api/withdrawal/status/${withdrawalId}`, { headers });
      
      if (response.data.success) {
        const status = response.data.withdrawal.status;
        setWithdrawalStatus(status);
        
        if (status === 'completed') {
          setShowSuccess(true);
          setShowCountdown(false);
        }
      }
    } catch (err) {
      console.error("Failed to check status:", err);
    }
  };

  // ✅ Submit withdrawal with JWT token
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!amount || !walletAddress || !cryptocurrency || !network) {
      setError("Please fill all fields");
      return;
    }

    const amountNum = parseFloat(amount);
    
    if (amountNum > maxWithdrawal) {
      setError(`Maximum withdrawal is $${maxWithdrawal.toFixed(2)} (your available profit)`);
      return;
    }

    if (amountNum < 50) {
      setError("Minimum withdrawal is $50");
      return;
    }

    if (walletAddress.length < 20) {
      setError("Please enter a valid wallet address");
      return;
    }

    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      
      const response = await axios.post(`${API_BASE}/api/withdrawal/request`, {
        amount: amountNum,
        walletAddress: walletAddress,
        cryptocurrency: cryptocurrency,
        network: network
      }, { headers });

      if (response.data.success) {
        setWithdrawalId(response.data.withdrawal.id);
        setCountdownEndsAt(response.data.withdrawal.countdownEndsAt);
        setShowCountdown(true);
        setWithdrawalStatus('pending');
        
        setAmount('');
        setWalletAddress('');
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit withdrawal request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetMax = () => {
    setAmount(maxWithdrawal.toFixed(2));
  };

  const handleCryptoChange = (value) => {
    setCryptocurrency(value);
    const crypto = cryptoOptions.find(c => c.value === value);
    if (crypto && crypto.networks.length > 0) {
      setNetwork(crypto.networks[0]);
    }
  };

  const getCryptoIcon = (crypto) => {
    const option = cryptoOptions.find(c => c.value === crypto);
    return option ? option.icon : '💰';
  };

  if (loading) {
    return (
      <div className="withdraw-container">
        <div className="loading-state">
          <RefreshCw className="spin" size={24} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="withdraw-container">
        <div className="error-state">
          <AlertCircle size={24} />
          <p>Please log in to withdraw funds.</p>
        </div>
      </div>
    );
  }

  // Success screen
  if (showSuccess) {
    return (
      <div className="withdraw-container">
        <div className="success-screen">
          <div className="success-icon">
            <CheckCircle2 size={64} />
          </div>
          <h2>Withdrawal Completed!</h2>
          <p>Your cryptocurrency has been successfully sent to your wallet.</p>
          <div className="success-details">
            <div className="detail-row">
              <span>Amount:</span>
              <strong>${parseFloat(amount).toFixed(2)}</strong>
            </div>
            <div className="detail-row">
              <span>Cryptocurrency:</span>
              <strong>{getCryptoIcon(cryptocurrency)} {cryptocurrency}</strong>
            </div>
            <div className="detail-row">
              <span>Network:</span>
              <strong>{network}</strong>
            </div>
            <div className="detail-row">
              <span>Wallet:</span>
              <strong>{walletAddress.substring(0, 8)}...{walletAddress.substring(walletAddress.length - 6)}</strong>
            </div>
          </div>
          <button 
            className="btn-primary"
            onClick={() => {
              setShowSuccess(false);
              loadUserData();
            }}
          >
            Make Another Withdrawal
          </button>
        </div>
      </div>
    );
  }

  // Countdown screen
  if (showCountdown) {
    return (
      <div className="withdraw-container">
        <div className="countdown-screen">
          <div className="countdown-header">
            <Clock size={48} />
            <h2>Processing Your Withdrawal</h2>
          </div>
          
          <div className="countdown-timer">
            <div className="time-unit">
              <span className="time-value">{String(timeRemaining.minutes).padStart(2, '0')}</span>
              <span className="time-label">Minutes</span>
            </div>
            <span className="time-separator">:</span>
            <div className="time-unit">
              <span className="time-value">{String(timeRemaining.seconds).padStart(2, '0')}</span>
              <span className="time-label">Seconds</span>
            </div>
          </div>

          <div className="countdown-info">
            <p>Your crypto withdrawal is being processed by our admin team.</p>
            <p>Please keep this window open. The timer will automatically update when your transfer is complete.</p>
          </div>

          <div className="countdown-details">
            <div className="detail-row">
              <span>Amount:</span>
              <strong>${parseFloat(amount).toFixed(2)}</strong>
            </div>
            <div className="detail-row">
              <span>Cryptocurrency:</span>
              <strong>{getCryptoIcon(cryptocurrency)} {cryptocurrency}</strong>
            </div>
            <div className="detail-row">
              <span>Network:</span>
              <strong>{network}</strong>
            </div>
            <div className="detail-row">
              <span>Wallet:</span>
              <strong>{walletAddress.substring(0, 8)}...{walletAddress.substring(walletAddress.length - 6)}</strong>
            </div>
            <div className="detail-row">
              <span>Status:</span>
              <strong className={`status-${withdrawalStatus}`}>
                {withdrawalStatus === 'pending' && '⏳ Pending'}
                {withdrawalStatus === 'processing' && '🔄 Processing'}
                {withdrawalStatus === 'completed' && '✅ Completed'}
              </strong>
            </div>
          </div>

          <div className="countdown-note">
            <Shield size={16} />
            <span>All transactions are secured and encrypted on the blockchain</span>
          </div>
        </div>
      </div>
    );
  }

  // Main withdrawal form
  return (
    <div className="withdraw-container">
      <div className="withdraw-header">
        <h2>Withdraw Profits</h2>
        <p className="withdraw-subtitle">Withdraw your earned profits to your crypto wallet</p>
        <button 
          onClick={handleRefresh}
          disabled={refreshing}
          style={{ 
            background: 'transparent', 
            border: '1px solid rgba(255,255,255,0.1)', 
            borderRadius: '6px', 
            padding: '6px 12px', 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: '#94a3b8',
            fontSize: '12px',
            marginTop: '8px'
          }}
        >
          <RefreshCw size={12} className={refreshing ? 'spin' : ''} />
          {refreshing ? 'Syncing...' : 'Refresh Balance'}
        </button>
      </div>
      
      <div className="balance-card">
        <div className="balance-info">
          <Unlock size={20} style={{ color: '#10b981' }} />
          <div>
            <span style={{ color: '#10b981' }}>Available to Withdraw (Profit)</span>
            <strong style={{ color: '#10b981' }}>${balance.toFixed(2)}</strong>
          </div>
        </div>
        
        {lockedBalance > 0 && (
          <div className="max-withdrawal" style={{ 
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            padding: '12px',
            borderRadius: '8px',
            marginTop: '12px'
          }}>
            <Lock size={16} style={{ color: '#f59e0b' }} />
            <div style={{ flex: 1 }}>
              <span style={{ color: '#f59e0b', fontSize: '12px' }}>Locked Investment Principal</span>
              <strong style={{ color: '#f59e0b' }}>${lockedBalance.toFixed(2)}</strong>
            </div>
          </div>
        )}
      </div>

      <div style={{
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start'
      }}>
        <Info size={20} style={{ color: '#60a5fa', flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: '1.6' }}>
          <strong style={{ color: '#60a5fa' }}>How Withdrawals Work:</strong><br />
          You can withdraw <strong style={{ color: '#10b981' }}>100% of your available profit</strong> at any time. 
          Your locked principal continues earning automatically and cannot be withdrawn. 
          This ensures your investment keeps growing perpetually.
        </div>
      </div>

      {!eligible && (
        <div className="eligibility-warning">
          <AlertCircle size={20} />
          <div>
            <strong>Not Eligible Yet</strong>
            <p>{eligibilityMessage}</p>
            {daysRemaining > 0 && (
              <p className="days-remaining">
                <Clock size={14} /> {daysRemaining} days remaining
              </p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="withdraw-form">
        <div className="form-section">
          <label>Withdrawal Amount (USD)</label>
          <div className="amount-input-group">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              step="0.01"
              min="50"
              max={maxWithdrawal}
              placeholder={`Min $50 • Max $${maxWithdrawal.toFixed(2)}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={!eligible}
              required
            />
            <button 
              type="button" 
              className="btn-max" 
              onClick={handleSetMax}
              disabled={!eligible}
            >
              MAX
            </button>
          </div>
          <p className="input-hint">You can withdraw up to 100% of your available profit</p>
        </div>

        <div className="form-section">
          <label><Coins size={14} /> Select Cryptocurrency</label>
          <div className="crypto-selector">
            {cryptoOptions.map((crypto) => (
              <button
                key={crypto.value}
                type="button"
                className={`crypto-option ${cryptocurrency === crypto.value ? 'active' : ''}`}
                onClick={() => handleCryptoChange(crypto.value)}
                disabled={!eligible}
              >
                <span className="crypto-icon">{crypto.icon}</span>
                <span className="crypto-name">{crypto.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="form-section">
          <label><Network size={14} /> Select Network</label>
          <div className="network-selector">
            {getNetworksForCrypto().map((net) => (
              <button
                key={net}
                type="button"
                className={`network-option ${network === net ? 'active' : ''}`}
                onClick={() => setNetwork(net)}
                disabled={!eligible}
              >
                {net}
              </button>
            ))}
          </div>
          <p className="input-hint">
            ⚠️ Make sure your wallet supports the {network} network. Sending to wrong network may result in permanent loss.
          </p>
        </div>

        <div className="form-section">
          <label><Wallet size={14} /> Wallet Address</label>
          <input
            type="text"
            placeholder={`Enter your ${cryptocurrency} ${network} wallet address`}
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            disabled={!eligible}
            required
          />
          <p className="input-hint">
            Double-check your wallet address. Transactions cannot be reversed.
          </p>
        </div>

        <button 
          type="submit" 
          className="btn-submit"
          disabled={submitting || !eligible || !amount || !walletAddress || !cryptocurrency || !network}
        >
          {submitting ? (
            <>
              <RefreshCw size={16} className="spin" />
              Processing...
            </>
          ) : (
            <>
              <ArrowRight size={16} />
              Submit Withdrawal Request
            </>
          )}
        </button>
      </form>

      <div className="withdraw-info">
        <div className="info-item">
          <Clock size={16} />
          <span>Processing time: 20 minutes max</span>
        </div>
        <div className="info-item">
          <Shield size={16} />
          <span>All transactions are secured on the blockchain</span>
        </div>
        <div className="info-item">
          <AlertCircle size={16} />
          <span>Verify wallet address before submitting</span>
        </div>
      </div>
    </div>
  );
};

export default Withdraw;